import { useCallback } from 'react';
import { NavigateFunction } from 'react-router-dom';
import type { ScenarioType as Scenario } from '../../../api/scenarios';
import { api } from '../../../api';
import type { CreateScenarioPayload } from '../../../api/types/scenario';

interface Notification {
  message: string;
  type: 'success' | 'error' | 'info';
}

interface UseDeathEventsOptions {
  scenario: Scenario | null;
  deathMessageId: string | null;
  navigate: NavigateFunction;
  resetPersonaDeathState: () => void;
  handleRewindToMessage: (messageId: string) => Promise<void>;
  setIsSubmitting: (isSubmitting: boolean) => void;
  setIsInitialLoad: (isInitialLoad: boolean) => void;
  setNotification: (notification: Notification | null) => void;
}

/**
 * Hook for handling death-related events in scenarios
 */
export function useDeathEvents({
  scenario,
  deathMessageId,
  navigate,
  resetPersonaDeathState,
  handleRewindToMessage,
  setIsSubmitting,
  setIsInitialLoad,
  setNotification
}: UseDeathEventsOptions) {
  
  /**
   * Handles the "Play Again" action by creating a new scenario
   */
  const handlePlayAgain = useCallback(async (): Promise<void> => {
    if (!scenario) return;
    
    try {
      setIsSubmitting(true);
      setIsInitialLoad(false);
      
      // Reset the persona death state to show input bar in the new game
      resetPersonaDeathState();
      
      // Create a completely new scenario to avoid leftover state
      const payload: CreateScenarioPayload = {
        definition: scenario.definition.id,
        persona: scenario.persona.id
      };
      
      // Find character if present in the original scenario's characters
      const mainCharacter = scenario.characters.find(char => 
        // Usually the first character is the main one, but we can add logic here if needed
        scenario.characters.length > 0
      );
      
      // Include character ID if we found one
      if (mainCharacter) {
        payload.character = mainCharacter.id;
      }
      
      // Create a fresh scenario
      const newScenario = await api.scenarios.create(payload);
      
      // Navigate to the new scenario
      navigate(`/sessions/${newScenario.id}`);
    } catch (error) {
      console.error('Failed to create new scenario:', error);
      
      // Show error notification
      setNotification({
        message: 'Failed to create new scenario. Please try again.',
        type: 'error'
      });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setIsSubmitting(false);
    }
  }, [scenario, navigate, setIsSubmitting, setIsInitialLoad, setNotification, resetPersonaDeathState]);

  /**
   * Handles the "Rewind Death" action by rewinding to the message before the death
   */
  const handleRewindDeath = useCallback(async (): Promise<void> => {
    if (!scenario || !deathMessageId) return;
    
    try {
      setIsSubmitting(true);
      setIsInitialLoad(false);
      
      // Find the message right before the death message
      const deathMessageIndex = scenario.messages.findIndex(msg => msg.id === deathMessageId);
      
      // If we found the death message
      if (deathMessageIndex > 0) {
        let rewindIndex: number;
        
        // Try to go back two messages if possible
        if (deathMessageIndex >= 2) {
          rewindIndex = deathMessageIndex - 2;
        } 
        // Otherwise fall back to one message before
        else {
          rewindIndex = deathMessageIndex - 1;
        }
        
        // Get the target message to rewind to
        const messageToRewindTo = scenario.messages[rewindIndex];
        
        // Rewind to that message
        await handleRewindToMessage(messageToRewindTo.id);
        
        // Reset the persona death state
        resetPersonaDeathState();
      } else {
        // If death message is the first message or not found, just restart
        await handlePlayAgain();
      }
    } catch (error) {
      console.error('Failed to rewind death:', error);
      
      // Show error notification
      setNotification({
        message: 'Failed to rewind. Please try again.',
        type: 'error'
      });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    scenario, 
    deathMessageId, 
    handleRewindToMessage, 
    resetPersonaDeathState, 
    handlePlayAgain,
    setIsSubmitting, 
    setIsInitialLoad, 
    setNotification
  ]);

  return {
    handlePlayAgain,
    handleRewindDeath
  };
}

export default useDeathEvents; 